// InfoComponent.jsx
import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../theme/ThemeContext';
import CloseIconBlack from '../static/images/black_icons/x_icon_black.png';
import CloseIconWhite from '../static/images/x_icon.png';
import PlaceholderImage from '../static/images/placeholder.png';
import SelectionGifLight from '../static/images/selection_light.gif';
import SelectionGifDark from '../static/images/selection_dark.gif';
import HorizontalColoredLine from './widgets/horizontal_line';

export function InfoComponent({ isOpen, onClose, activeSection }) {
    const { theme } = useTheme();
    const CloseIcon = theme.name === 'light' ? CloseIconBlack : CloseIconWhite;
    const SelectionGif = theme.name === 'dark' ? SelectionGifDark : SelectionGifLight;
    const [currentSection, setCurrentSection] = useState('');
    const contentRef = useRef(null);

    const sectionRefs = {
        general: useRef(null),
        help: useRef(null),
        uploadedOTUs: useRef(null),
    };

    const sectionTitles = {
        general: 'General Information',
        help: 'Help',
        uploadedOTUs: 'Uploaded OTUs',
    };

    useEffect(() => {

        if (isOpen && activeSection && sectionRefs[activeSection]?.current) {
            const yOffset = -60;
            const element = sectionRefs[activeSection].current;
            const y = element.getBoundingClientRect().top + window.scrollY + yOffset;

            contentRef.current.scrollTo({
                top: y,
                behavior: 'smooth'
            });
        }
    }, [isOpen, activeSection]);

    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = contentRef.current.scrollTop;
            const sections = Object.entries(sectionRefs);

            if (scrollPosition < 50) {
                setCurrentSection('');
                return;
            }

            for (let i = sections.length - 1; i >= 0; i--) {
                const [key, ref] = sections[i];
                if (ref.current && ref.current.offsetTop <= scrollPosition + 50) {
                    setCurrentSection(key);
                    break;
                }
            }
        };

        const contentEl = contentRef.current;
        if (contentEl) {
            contentEl.addEventListener('scroll', handleScroll);
            handleScroll();
        }

        return () => {
            if (contentEl) {
                contentEl.removeEventListener('scroll', handleScroll);
            }
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return createPortal(
        <div
            onClick={handleOverlayClick}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 1000,
                backdropFilter: 'blur(5px)',
                WebkitBackdropFilter: 'blur(5px)',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }}>
            <div style={{
                position: 'relative',
                width: '80%',
                maxWidth: '800px',
                height: '80vh',
                margin: '5vh auto',
                backgroundColor: theme.colors.background,
                color: theme.colors.text,
                borderRadius: '10px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
            }}>
                <div style={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: theme.colors.surface,
                    padding: '15px 20px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: `1px solid ${theme.colors.border}`,
                    zIndex: 2,
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                    }}>
                        <h2 style={{ margin: 0 }}>
                            {sectionTitles[currentSection] || 'Information'}
                        </h2>
                    </div>
                    <img
                        src={CloseIcon}
                        alt="Close"
                        style={{
                            width: '20px',
                            height: '20px',
                            cursor: 'pointer',
                        }}
                        onClick={onClose}
                    />
                </div>

                <div
                    ref={contentRef}
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        padding: '20px',
                        paddingBottom: '100px',

                    }}
                >
                    <div ref={sectionRefs.general}>
                        <h2>Microverse Mapper</h2>
                        <p>The <b>Microverse Mapper</b> shows how our microbial world is structured.
                            The Microverse is the globally connected network of microbiomes that covers the Earth and all things on it.
                            Microbiomes are communities of microbial organisms, and they can be studied with
                            {' '}<a
                                href={`https://en.wikipedia.org/wiki/Metagenomics`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: theme.colors.text }}
                                onClick={(e) => e.stopPropagation()}
                            >metagenomics</a>.
                            Over the past decades, the global research community has analyzed hundreds of thousands of microbiomes from a wide range of ecosystems.
                            With the <b>Microverse Mapper</b>, you can browse them on a selection of maps.
                        </p>
                        <p>Bugs, issues, and feature requests may be communicated through
                            {' '}<a
                                href={`https://docs.google.com/forms/d/e/1FAIpQLSesS7n9Q1ukTp8X8P2UjjrPQe5aO_mFpnr7B6bfQndV_nVK7w/viewform?usp=dialog`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: theme.colors.text }}
                                onClick={(e) => e.stopPropagation()}
                            >this form</a>.
                        </p>
                    </div>
                    <HorizontalColoredLine />
                    <div ref={sectionRefs.help}>
                        <h2>Help</h2>
                        <p>This Help page may be accessed by clicking the question mark icon in the left menu.
                        </p>
                        <p>
                            <h3>Selecting microbiomes</h3>
                            <img
                                src={SelectionGif}
                                alt="lasso selection"
                                style={{
                                    // width: '20%',
                                    width: '15%',
                                    // height: '20%',
                                    height: '15%',
                                    float: 'right',
                                    // marginLeft: 20,
                                    marginRight: 20,
                                    borderRadius: 10,

                                    borderStyle: 'solid',
                                    borderWidth: 1,
                                    borderColor: theme.colors.border,
                                    // margin: 20,
                                }}
                            />
                            There are several ways to select microbiomes (points on the map).
                            <ul style={{ listStyleType: 'disc', paddingLeft: '20px' }}>
                                <li>Individual points can be selected by clicking them.</li>
                                <li>Lasso selection (shift-drag) selects multiple points.</li>
                                <li>You can also click items in the color legend to select all points with that label.</li>
                                <li>Points can be added to your current selection by holding the command-key (Mac) or ctrl-key (Windows).</li>
                                <li>A sub-selection of points within your current selection can be made by holding the shift + option keys (Mac).</li>
                            </ul>
                            <h3>Search function</h3>
                            Alternatively, you can use the search function to identify specific microbiomes.
                            <ul style={{ listStyleType: 'disc', paddingLeft: '20px' }}>
                                <li><b>Smart search</b> allows you to find microbiomes that match a specific keyword, including <b>taxon names</b> or <b>sample identifiers</b>.
                                    Smart search has auto-complete so you can easily see if your query matches the database.</li>
                                <li><b>Advanced search</b> allows you to build complex queries, as shown in the following examples.
                                    <br />Find microbiomes with at least 10 <i>Veillonellales</i> or <i>Lentisphaerales</i> reads:
                                    <pre style={{ textAlign: 'left', marginLeft: 10, marginRight: 10 }}>
                                        <code>
                                            {
                                                `(Veillonellales,Lentisphaerales)[taxonomy](10)[threshold_count]`
                                            }
                                        </code>
                                    </pre>
                                    <br />Find microbiomes where Candidatus <i>Saccharibacteria</i> has a relative abundance of at least 0.005:
                                    <pre style={{ textAlign: 'left', marginLeft: 10, marginRight: 10 }}>
                                        <code>
                                            {
                                                `(Candidatus Saccharibacteria)[taxonomy](0.005)[threshold_abundance]`
                                            }
                                        </code>
                                    </pre>
                                    <br />Find microbiomes where the biome annotation matches either "root:Environmental:Aquatic" or "root:Environmental:Aquatic:Freshwater:Lake":
                                    <pre style={{ textAlign: 'left', marginLeft: 10, marginRight: 10 }}>
                                        <code>
                                            {
                                                `(root:Environmental:Aquatic,root:Environmental:Aquatic:Freshwater:Lake)[biome]`
                                            }
                                        </code>
                                    </pre>
                                    <br />Find microbiomes with specific MGnify identifiers:
                                    <pre style={{ textAlign: 'left', marginLeft: 10, marginRight: 10 }}>
                                        <code>
                                            {
                                                `(SRR6417114, SRR6408791, ERR1530766, ERR1598988)[mgnify_id]`
                                            }
                                        </code>
                                    </pre>
                                </li>
                            </ul>
                            <div ref={sectionRefs.uploadedOTUs}>
                                <h3>Placing your own samples on the map</h3>
                                The microbiome data in the <b>Microverse Mapper</b> is mined from publicly accessible microbiome databases including
                                {' '}<a
                                    href={`https://www.ebi.ac.uk/metagenomics/`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: theme.colors.text }}
                                    onClick={(e) => e.stopPropagation()}
                                > EBI MGnify</a>.
                                You can also place your own microbiome datasets into this global context.
                                <ul style={{ listStyleType: 'disc', paddingLeft: '20px' }}>
                                    <li>Create an OTU table using your favorite tool. It needs to be structured according to the
                                        {' '}<a
                                            href={`http://biom-format.org/documentation/biom_format.html`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: theme.colors.text }}
                                            onClick={(e) => e.stopPropagation()}
                                        > BIOM format</a>:
                                        {/* <pre> */}
                                        <pre style={{ textAlign: 'left', marginLeft: 10, marginRight: 10 }}>
                                            <code>
                                                {`# Taxonomy	Sample1	Sample2    ...
k__Bacteria;p__Firmicutes	1234	3456    ...
k__Bacteria;p__Bacteroidetes	345	567    ...
...`
                                                }
                                            </code>
                                        </pre></li>
                                    <li>Upload it by clicking the upload icon in the left menu.</li>
                                </ul>
                            </div>
                        </p>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
